<section class="flex flex-col gap-2 px-5 mx-auto pt-5 md:p-8 bg-white">
  <div class="mb-6 flex justify-between items-center">
    <div>
      <h2 class="text-3xl font-bold text-gray-900">Voice AI Dashboard</h2>
      <p class="text-gray-600">View your linked Voice AI items</p>
    </div>

    <mat-select
      placeholder="Choose Client"
      class="w-16 px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200 text-foreground placeholder:text-muted-foreground"
      [formControl]="selectedClientControl"
      multiple
    >
      <mat-option [value]="null" class="no-checkbox-option">
        <div
          class="px-4 py-1 cursor-pointer hover:bg-gray-100 font-medium"
          (click)="selectAllClient(); $event.stopPropagation()"
        >
          Select All
        </div>
      </mat-option>

      @for (item of clientsList; track item.clientId) {
      <mat-option [value]="item.clientId">
        {{ item.name }}
      </mat-option>
      }
    </mat-select>
  </div>

  <section id="lower" class="w-full max-md:max-w-full flex flex-col gap-4">
    <div class="ml-auto">
      <!-- Updated dropdown with select element -->
      <div class="relative">
        <select
          (change)="onTimePeriodChange($event, 'card')"
          class="flex gap-3 items-center justify-between px-3 rounded py-1 pr-8 cursor-pointer border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200 text-gray-600 appearance-none"
        >
          <option hidden value="last30Days">Last 30 Days</option>

          @for (period of timePeriods; track period.value) {
          <option [value]="period.value">{{ period.label }}</option>
          }
        </select>
        <img
          loading="lazy"
          src="assets/icons/Downward-Icon.svg"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 aspect-square pointer-events-none"
          alt="Drop-Down"
        />
      </div>
    </div>
    <div class="w-full max-md:max-w-full flex flex-wrap gap-4">
      @for (item of cards; track $index) {
      <ng-container
        [ngTemplateOutlet]="card"
        [ngTemplateOutletContext]="{ info: { item, index: $index } }"
      />
      }
    </div>
  </section>

  <!-- DASHBOARD CHART SECTION -->
  <section class="flex flex-wrap gap-6 mt-8">
    <!-- Outbound Calls -->
    <ng-container
      [ngTemplateOutlet]="chart"
      [ngTemplateOutletContext]="{
        data: {
          title: 'Outbound Calls',
          type: 'line',
          data: outboundChartData,
          labels: outboundChartData.labels,
          options: outboundAndConcurrentChartOptions,
          legend: false,
          key: Features.dashboard.graphs.outboundCallsByDate
        }
      }"
    ></ng-container>

    <!-- Connection Rate -->
    <ng-container
      [ngTemplateOutlet]="chart"
      [ngTemplateOutletContext]="{
        data: {
          title: 'Connection Rate',
          type: 'line',
          data: connectionRateChartData,
          labels: connectionRateChartData.labels,
          options: connectionRateChartOptions,
          legend: false,
          key: Features.dashboard.graphs.connectionRateByDate
        }
      }"
    ></ng-container>

    <!-- Concurrent Calls -->
    <ng-container
      [ngTemplateOutlet]="chart"
      [ngTemplateOutletContext]="{
        data: {
          title: 'Concurrent Calls',
          type: 'line',
          data: concurrentCallsChartData,
          labels: concurrentCallsChartData.labels,
          options: outboundAndConcurrentChartOptions,
          legend: false,
          key: Features.dashboard.graphs.concurrentCallsByDate
        }
      }"
    ></ng-container>
  </section>
</section>

<ng-template #card let-data="info">
  <div
    *hasFeature="{
      screen: 'dashboard',
      key: data.item.key.toLowerCase(),
      action: 'remove'
    }"
    class="cards p-4 shadow-xl rounded-lg min-w-[200px] flex-1 card-{{
      data.index + 1
    }} hover:scale-105 transition-transform duration-300"
  >
    <h3 class="text-lg font-medium card-heading">{{ data.item.label }}</h3>
    <div class="card-icon grid place-items-center aspect-square w-16">
      <img
        loading="lazy"
        src="assets/icons/{{ data.item.icon }}"
        class="aspect-square h-3/5 icon-black"
        alt="Account-Info-Icon"
      />
    </div>
    <div class="flex flex-col gap-1 card-content">
      <div class="text-2xl font-bold">{{ data.item.total }}</div>
      <!-- <div class="text-sm font-medium leading-5 text-[#626F86]">
        {{ data.item.percentage }}% new since last month
      </div> -->
    </div>
  </div>
</ng-template>

<ng-template #chart let-item="data">
  <div
    *hasFeature="{
      screen: 'dashboard',
      key: item.key.toLowerCase(),
      action: 'remove'
    }"
    id="graph"
    class="relative bg-white p-5 rounded-lg shadow-lg flex-1 max-sm:min-w-[290px] sm:min-w-[500px]"
  >
    <div class="text-lg font-semibold mb-4 flex justify-between items-center">
      {{ item.title }}
      <!-- Updated dropdown with select element -->
      <div class="relative">
        <select
          (change)="onTimePeriodChange($event, item.title)"
          class="flex gap-3 items-center justify-between px-3 rounded py-1 pr-8 cursor-pointer border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200 text-gray-600 appearance-none"
        >
          <option hidden value="last30Days">Last 30 Days</option>
          @for (period of timePeriods; track period.value) {
          <option [value]="period.value">{{ period.label }}</option>
          }
        </select>
        <img
          loading="lazy"
          src="assets/icons/Downward-Icon.svg"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 aspect-square pointer-events-none"
          alt="Drop-Down"
        />
      </div>
    </div>

    <!-- Chart canvas with wrapper to control height -->
    <div class="chart-wrapper">
      <canvas
        baseChart
        [data]="item.data"
        [labels]="item.labels"
        [options]="item.options"
        [legend]="item.legend"
        [type]="item.type"
      >
      </canvas>
    </div>

    <!-- No data overlay -->
    @if(item.data && item.data.datasets[0].data.length == 0) {
    <div class="overlay-placeholder">
      <p class="text-center">No data available to display the chart.</p>
    </div>
    }
  </div>
</ng-template>
